<template>
  <div class="reference-axis-property" data-testid="axis-property-section">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="选择" name="selection">
        <div class="group-block">
          <SelectionListFrame
            :items="selectionSummary"
            :active-index="activeEntryIndex"
            item-key-field="entityId"
            empty-text="请选择参考"
            context-menu-enabled
            delete-all-label="消除选择"
            list-role="list"
            @update:active-index="setActiveEntry"
            @delete-all="handleClearSelection"
            @delete-item="handleDeleteSelection"
          />
        </div>
      </n-collapse-item>

      <n-collapse-item title="模式" name="mode">
        <div class="group-block">
          <n-radio-group :value="currentMode" size="small" @update:value="setMode">
            <n-space vertical size="small">
              <n-radio v-for="item in modeItems" :key="item.mode" :value="item.mode">
                {{ item.label }}
              </n-radio>
            </n-space>
          </n-radio-group>
        </div>
      </n-collapse-item>

      <n-collapse-item title="诊断" name="diagnostic">
        <div class="diagnostic">{{ diagnosticText }}</div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { NCollapse, NCollapseItem, NRadio, NRadioGroup, NSpace } from 'naive-ui';
import { AxisMode } from '@puppycad/cad-core';
import { useViewer } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';

/**
 * @description 基准轴选择条目结构。
 */
interface AxisSelectionEntry {
  /**
   * @description 实体 ID。
   */
  entityId: string;
  /**
   * @description 展示标签。
   */
  label: string;
}

defineOptions({
  inheritAttrs: false,
});

const { getViewer } = useViewer();

/**
 * @description 面板本地快照。
 */
const localSnapshot = ref<{ mode: AxisMode; selectionSummary: AxisSelectionEntry[]; errorMessage: string }>({
  mode: AxisMode.LineEdgeAxis,
  selectionSummary: [],
  errorMessage: '等待输入有效引用',
});


/**
 * @description 从渲染器桥接同步当前快照。
 */
function syncFromRenderer(): void {
  const runtime = getViewer()
  const controller = runtime?.referenceAxisController;
  if (!controller) {
    localSnapshot.value = {
      mode: AxisMode.LineEdgeAxis,
      selectionSummary: [],
      errorMessage: '等待输入有效引用',
    };
    return;
  }
  const viewModel = controller.getViewModel();
  localSnapshot.value = {
    mode: viewModel.state.mode,
    selectionSummary: (viewModel.selectionSummary ?? []).slice(0, 2),
    errorMessage: viewModel.state.preview?.error?.message ?? '等待输入有效引用',
  };
}

let axisStateSyncTimer: number | null = null;

onMounted(() => {
  syncFromRenderer();
  
  /**
   * 通过轻量轮询同步控制器状态，保证“视口选择 -> 面板刷新”链路实时可见。
   */
  axisStateSyncTimer = window.setInterval(() => {
    syncFromRenderer();
  }, 120);
  
});

onBeforeUnmount(() => {
  if (axisStateSyncTimer !== null) {
    clearInterval(axisStateSyncTimer);
    axisStateSyncTimer = null;
  }
});

/**
 * @description 默认展开分组。
 */
const defaultExpanded = ['selection', 'mode', 'diagnostic'];

/**
 * @description 当前基准轴模式。
 */
const currentMode = computed(() => localSnapshot.value.mode ?? AxisMode.LineEdgeAxis);

/**
 * @description 当前选择摘要列表（最多 2 条）。
 */
const selectionSummary = computed<AxisSelectionEntry[]>(() => (
  (localSnapshot.value.selectionSummary ?? []).slice(0, 2)
));

/**
 * @description 诊断文案。
 */
const diagnosticText = computed(() => localSnapshot.value.errorMessage ?? '等待输入有效引用');

/**
 * @description 当前激活选择条目索引。
 */
const activeEntryIndex = ref(0);

/**
 * @description 基准轴模式列表。
 */
const modeItems: Array<{ mode: AxisMode; label: string }> = [
  { mode: AxisMode.LineEdgeAxis, label: '一直线/边线/轴' },
  { mode: AxisMode.TwoPlanes, label: '两平面' },
  { mode: AxisMode.TwoPoints, label: '两点/顶点' },
  { mode: AxisMode.CylOrConeFace, label: '圆柱/圆锥面' },
  { mode: AxisMode.PointAndFaceOrPlane, label: '点和面/基准面' },
];

/**
 * @description 监听条目列表变化并同步激活项。
 * 规则：
 * 1. 无条目时重置到 0；
 * 2. 有条目时默认激活最后一项（后选中优先）。
 */
watch(
  selectionSummary,
  (nextEntries) => {
    if (nextEntries.length === 0) {
      activeEntryIndex.value = 0;
      return;
    }

    // 统一采用“后选中优先”的激活策略。
    activeEntryIndex.value = nextEntries.length - 1;
  },
  { immediate: true }
);

/**
 * @description 设置当前模式。
 * @param mode 目标模式。
 */
function setMode(mode: AxisMode): void {
  const runtime = getViewer()
  const controller = runtime?.referenceAxisController;
  if (!controller) {
    return;
  }
  controller.setMode(mode);
  controller.syncSelectionVisuals();
  syncFromRenderer();
}

/**
 * @description 设置激活条目索引。
 * @param index 条目索引。
 */
function setActiveEntry(index: number): void {
  activeEntryIndex.value = index;
}

function handleClearSelection(): void {
  const runtime = getViewer();
  const controller = runtime?.referenceAxisController;
  controller?.clearSelections();
  controller?.syncSelectionVisuals();
  syncFromRenderer();
}

function handleDeleteSelection(payload: { item: unknown; index: number }): void {
  const entry = payload.item as AxisSelectionEntry | undefined;
  if (!entry?.entityId) {
    return;
  }
  const runtime = getViewer()
  const controller = runtime?.referenceAxisController;
  controller?.removeSelection(entry.entityId);
  controller?.syncSelectionVisuals();
  syncFromRenderer();
}

</script>

<style scoped>
.reference-axis-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.group-block + .group-block {
  margin-top: 12px;
}

/*------ 基准轴主题适配 开始------*/
.diagnostic {
  color: var(--n-text-color-2);
  font-size: 12px;
}

:deep(.n-radio .n-radio__label) {
  color: var(--n-text-color);
}
/*------ 基准轴主题适配 结束------*/
</style>
